import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React from 'react';
import { theme } from '../styles/theme';
import { DOC } from '../utils/api';
import {
  CustomerDocument,
  fetchCustomerDocument,
  isCustomerDocument,
} from '../utils/documentLinks';

const styles = makeStyles<typeof theme, { fullWidth: boolean }>({
  stretch: {
    position: 'relative',
    width: '1280px',
    maxWidth: '100%',
  },
  frame: {
    display: 'block',
    width: '100%',
    border: '0 none',
    height: 'calc(100vh -  (8px * 18))',
    position: 'relative',
    background: 'transparent',
  },
  spinner: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    marginLeft: '-24px',
    marginTop: '-24px',
  },
});

const RETRY_MS = 5000;

interface DocumentProps {
  /**
   * A bundled static PDF (terms, faktablad, …), or a customer document held by
   * the engine. The latter needs the bearer token, which an `<iframe src>`
   * cannot send, so it is fetched and shown from a blob URL.
   */
  path: string | CustomerDocument;
  fullWidth?: false;
}

/**
 * The blob URL of a customer document, or `undefined` while it is on its way.
 * A freshly bought product has no PDF yet — the engine answers 404 until the
 * PDF service has rendered it — so a 404 is retried; anything else is final.
 */
const useDocumentBlob = (document?: CustomerDocument) => {
  const [url, setUrl] = React.useState<string>();
  const { accountId, related } = document || {};

  React.useEffect(() => {
    if (!accountId || !related) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let created: string | undefined;

    const load = async () => {
      try {
        const blob = await fetchCustomerDocument({ accountId, related });
        if (!cancelled) {
          created = URL.createObjectURL(blob);
          setUrl(created);
        }
      } catch (e) {
        if (!cancelled && e instanceof Response && e.status === 404) {
          timer = setTimeout(load, RETRY_MS);
        } else if (!cancelled) {
          console.error(e);
        }
      }
    };

    setUrl(undefined);
    void load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (created) {
        URL.revokeObjectURL(created);
      }
    };
  }, [accountId, related]);

  return url;
};

const StaticDocument: React.FC<{ path: string; frame: string }> = ({
  path,
  frame,
  children,
}) => {
  const [loading, setLoading] = React.useState(true);
  const src = new URL(path, DOC).toString();

  React.useEffect(() => setLoading(true), [src]);

  return (
    <>
      {loading && children}
      <iframe src={src} className={frame} onLoad={() => setLoading(false)} />
    </>
  );
};

export const Document: React.FC<DocumentProps> = (props) => {
  const { path, fullWidth = true } = props;
  const { stretch, frame, spinner } = styles({ fullWidth });
  const blobUrl = useDocumentBlob(isCustomerDocument(path) ? path : undefined);

  const progress = (
    <CircularProgress
      variant="indeterminate"
      color="secondary"
      size={48}
      className={spinner}
    />
  );

  return (
    <Box className={stretch}>
      {isCustomerDocument(path) ? (
        blobUrl ? (
          <iframe src={blobUrl} className={frame} />
        ) : (
          progress
        )
      ) : (
        <StaticDocument path={path} frame={frame}>
          {progress}
        </StaticDocument>
      )}
    </Box>
  );
};
